import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  useToast,
} from "@chakra-ui/react";
import { MdLockOpen, MdLock } from "react-icons/md"; // Import lock and unlock icons
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";

export const ClientList = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [manualUserId, setManualUserId] = useState(""); // State for manual user ID
  const [error, setError] = useState("");
  const toast = useToast();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL3}/users/limited`
        );
        if (!response) {
          throw new Error("Failed to fetch users");
        }
        setUsers(response.users);
        setFilteredUsers(response.users);
      } catch (error) {
        console.error("Error fetching users:", error);
        setError("Failed to fetch users");
      }
    };

    fetchUsers();
  }, []);

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const filtered = users.filter((user) =>
      user.user_id.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  const handleToggleBlockStatus = async (userId, isBlocked) => {
    const endpoint = isBlocked
      ? `${process.env.REACT_APP_API_URL3}/users/unblock/${userId}`
      : `${process.env.REACT_APP_API_URL3}/users/block/${userId}`;
    const action = isBlocked ? "unblocked" : "blocked";

    try {
      const response = await fetchWithToken(endpoint, "POST");
      if (!response) {
        throw new Error(`Failed to ${action} user`);
      }
      toast({
        title: "Success",
        description: `User ${userId} has been ${action}.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Update the user's status in the table
      const updatedUsers = users.map((user) =>
        user.user_id === userId ? { ...user, is_blocked: !isBlocked } : user
      );
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
    } catch (error) {
      console.error(`Error ${action} user:`, error);
      toast({
        title: "Error",
        description: `Failed to ${action} user.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleManualBlock = async () => {
    if (!manualUserId) {
      toast({
        title: "Error",
        description: "Please enter a valid user ID.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL3}/users/block/${manualUserId}`,
        "POST"
      );
      if (!response) {
        throw new Error("Failed to block user");
      }
      toast({
        title: "Success",
        description: `User ${manualUserId} has been blocked.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Optionally update the UI if the user exists in the current list
      const updatedUsers = users.map((user) =>
        user.user_id === manualUserId ? { ...user, is_blocked: true } : user
      );
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      setManualUserId(""); // Clear the input field
    } catch (error) {
      console.error("Error blocking user:", error);
      toast({
        title: "Error",
        description: "Failed to block user.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Card
      direction="column"
      w="100%"
      px="0px"
      overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      <Flex px="25px" justify="space-between" mb="20px" align="center">
        <Text
          color="gray.800"
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Client List
        </Text>
        <Input
          placeholder="Search user..."
          value={searchQuery}
          onChange={handleSearch}
          size="sm"
          w="200px"
        />
      </Flex>
      <Flex px="25px" mb="20px" align="center">
        <Input
          placeholder="Enter User ID to block"
          value={manualUserId}
          onChange={(e) => setManualUserId(e.target.value)}
          size="sm"
          w="200px"
          mr="10px"
        />
        <Button colorScheme="red" onClick={handleManualBlock}>
          Block User
        </Button>
      </Flex>
      <Box overflowY="auto" maxHeight="400px">
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            <Tr bg="rgb(69, 50, 240)">
              <Th textColor="white">User ID</Th>
              <Th textColor="white">Is Limited</Th>
              <Th textColor="white">Is Blocked</Th>
              <Th textColor="white">Prompt Tokens</Th>
              <Th textColor="white">Completion Tokens</Th>
              <Th textColor="white">Total Tokens</Th>
              <Th textColor="white">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredUsers.map((user) => (
              <Tr key={user.user_id}>
                <Td>{user.user_id}</Td>
                <Td>{user.is_limited ? "Yes" : "No"}</Td>
                <Td>{user.is_blocked ? "Yes" : "No"}</Td>
                <Td>{user.usage.prompt_tokens}</Td>
                <Td>{user.usage.completion_tokens}</Td>
                <Td>{user.usage.total_tokens}</Td>
                <Td>
                  <Button
                    size="sm"
                    colorScheme={user.is_blocked ? "green" : "red"}
                    leftIcon={user.is_blocked ? <MdLockOpen /> : <MdLock />}
                    onClick={() =>
                      handleToggleBlockStatus(user.user_id, user.is_blocked)
                    }
                  >
                    {user.is_blocked ? "Unblock" : "Block"}
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {error && (
        <Box mt="4">
          <Text color="red.500">{error}</Text>
        </Box>
      )}
    </Card>
  );
};
